import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Drawer,
  Descriptions,
  Input,
  Select,
  Space,
  Switch,
  Tabs,
  Typography,
  Alert,
  Spin,
  message,
} from "antd";
import moment from "moment";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import TeamLeaveApprovalsService from "Services/EmployeeServices/TeamLeaveApprovalsService";
import { LeaveTypeEnumLabel } from "Constants/Enums/LeavePolicyEnum";
import { LeaveRequestEnumLabel } from "Constants/Enums/LeaveRequestEnum";

const { Text } = Typography;
const { TextArea } = Input;

const fmt = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");

// Point 16: paid/unpaid split on each request. Prefer hours, fall back to days.
const fmtHrs = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const splitText = (r, kind) => {
  const days = kind === "paid" ? r?.paidDays : r?.unpaidDays;
  const hours = kind === "paid" ? r?.paidHours : r?.unpaidHours;
  if (days === undefined && hours === undefined) return null;
  const h = fmtHrs(hours);
  return h !== null ? `${days ?? "-"} days (${h} hrs)` : `${days ?? "-"} days`;
};

const splitTags = (r) => {
  if (r?.paidDays === undefined && r?.unpaidDays === undefined) return "-";
  return (
    <Space size={4} wrap>
      <Tag color="green">{r.paidDays ?? 0} paid</Tag>
      {Number(r.unpaidDays) > 0 && <Tag color="red">{r.unpaidDays} unpaid</Tag>}
    </Space>
  );
};

// AI coverage-risk level -> Alert type (Jun-21 AI coverage-warning handoff).
const riskAlertType = (level) => {
  const l = String(level || "").toLowerCase();
  if (l === "high") return "error";
  if (l === "medium") return "warning";
  if (l === "low") return "success";
  return "info";
};

// The coverage-risk AI sometimes returns riskLevel: null even with a full insight.
// Derive a sensible level from the coverage numbers so the header never reads "Unknown".
const resolveCoverageRiskLevel = (availability) => {
  const insight = availability?.coverageRiskInsight || {};
  if (insight.riskLevel) return String(insight.riskLevel).toLowerCase();

  const payload = availability?.coverageRiskRequestPayload || {};
  const remaining = payload.remainingAfterApproval;
  const minSafe = payload.team?.minSafe;
  const hasSkillGap = (payload.uncoveredSkills?.length || 0) > 0;
  const hasOverlap = (insight.overlapEmployees?.length || 0) > 0;

  if (remaining == null || minSafe == null) return null;
  if (remaining < minSafe) return "high";
  if (remaining === minSafe || hasSkillGap || hasOverlap) return "medium";
  return "low";
};

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const LEAVE_STATUS_COLORS = {
  Pending: "gold",
  Approved: "green",
  Rejected: "red",
  Cancelled: "default",
};
const leaveStatusTag = (v) => {
  if (v === null || v === undefined || v === "") return <Tag>-</Tag>;
  // Status can come back as a number (LeaveRequestEnum) or already a string label.
  const label = LeaveRequestEnumLabel[v] || (typeof v === "string" ? v : String(v));
  return <Tag color={LEAVE_STATUS_COLORS[label] || "default"}>{label}</Tag>;
};

const YEAR_OPTIONS = [2024, 2025, 2026, 2027].map((y) => ({ value: y, label: String(y) }));
const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Cancelled", label: "Cancelled" },
];

const TeamLeaveApprovals = () => {
  const {
    pendingRequests,
    loading,
    reviewing,
    fetchPendingApprovals,
    fetchTeamAvailability,
    submitReview,
    fetchTeamRequests,
  } = TeamLeaveApprovalsService();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [remarks, setRemarks] = useState("");

  const [activeTab, setActiveTab] = useState("pending");
  const [teamRequests, setTeamRequests] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filterYear, setFilterYear] = useState(2026);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUpcoming, setFilterUpcoming] = useState(false);

  useEffect(() => {
    fetchPendingApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Team Leave History tab: load (and reload on filter change) only while that
  // tab is active, using the Supervisor/Team-Requests endpoint.
  useEffect(() => {
    if (activeTab !== "history") return;
    setHistoryLoading(true);
    const params = [];
    if (filterYear) params.push(`year=${filterYear}`);
    if (filterStatus) params.push(`status=${filterStatus}`);
    if (filterUpcoming) params.push("upcomingOnly=true");
    fetchTeamRequests(params.join("&"))
      .then((data) => setTeamRequests(Array.isArray(data) ? data : []))
      .catch(() => setTeamRequests([]))
      .finally(() => setHistoryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterYear, filterStatus, filterUpcoming]);

  const openRequest = async (record) => {
    setSelected(record);
    setRemarks("");
    setAvailability(null);
    setDrawerOpen(true);
    try {
      setAvailabilityLoading(true);
      const data = await fetchTeamAvailability(record.leaveRequestId ?? record.id);
      setAvailability(data);
    } catch (e) {
      // Availability is a non-blocking check. If it fails to load, the
      // supervisor can still approve; we just don't show the overlap box.
      console.error("Error fetching team availability:", e);
      setAvailability(null);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
    setAvailability(null);
    setRemarks("");
  };

  const handleReview = async (isApproved) => {
    if (!selected) return;
    if (!isApproved && !remarks.trim()) {
      message.warning("Please add a remark explaining the rejection");
      return;
    }
    try {
      await submitReview({
        leaveRequestId: selected.leaveRequestId ?? selected.id,
        isApproved,
        approverRemarks: remarks.trim() || (isApproved ? "Approved" : "Rejected"),
      });
      closeDrawer();
      fetchPendingApprovals();
    } catch (e) {
      // Error already surfaced by the service.
    }
  };

  const employeeName = (r) =>
    r?.employeeName ||
    r?.requestedEmployeeName ||
    r?.employeeFullName ||
    r?.fullName ||
    r?.name ||
    [r?.firstName, r?.lastName].filter(Boolean).join(" ") ||
    [r?.employee?.firstName, r?.employee?.lastName].filter(Boolean).join(" ") ||
    r?.employee?.fullName ||
    "-";
  const dayCount = (r) => r?.totalDays ?? r?.days ?? "-";
  const deptName = (r) =>
    r?.departmentName ||
    r?.employee?.departmentName ||
    r?.department?.name ||
    r?.department ||
    r?.employee?.department ||
    "-";

  const columns = [
    { title: "Employee", key: "employee", render: (_, r) => employeeName(r) },
    { title: "Department", key: "department", render: (_, r) => deptName(r) },
    {
      title: "Leave Type",
      dataIndex: "leaveType",
      key: "leaveType",
      render: (v, r) => r.leaveTypeName || r.policyName || LeaveTypeEnumLabel[v] || v || "-",
    },
    { title: "From", key: "from", render: (_, r) => fmt(r.fromDate) },
    { title: "To", key: "to", render: (_, r) => fmt(r.toDate) },
    { title: "Days", key: "days", render: (_, r) => dayCount(r) },
    { title: "Paid / Unpaid", key: "split", render: (_, r) => splitTags(r) },
    { title: "Reason", dataIndex: "reason", key: "reason", render: (v) => v || "-" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag>{typeof v === "string" ? v : "Pending"}</Tag>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Button type="link" onClick={() => openRequest(r)}>
          Review
        </Button>
      ),
    },
  ];

  const historyColumns = [
    { title: "Employee", key: "h-emp", render: (_, r) => employeeName(r) },
    { title: "Department", key: "h-department", render: (_, r) => deptName(r) },
    {
      title: "Leave Type",
      key: "h-type",
      render: (_, r) =>
        r.leaveTypeName || r.policyName || LeaveTypeEnumLabel[r.leaveType] || r.leaveType || "-",
    },
    { title: "From", key: "h-from", render: (_, r) => fmt(r.fromDate) },
    { title: "To", key: "h-to", render: (_, r) => fmt(r.toDate) },
    { title: "Days", key: "h-days", render: (_, r) => dayCount(r) },
    { title: "Paid / Unpaid", key: "h-split", render: (_, r) => splitTags(r) },
    { title: "Status", key: "h-status", render: (_, r) => leaveStatusTag(r.status) },
    { title: "Reason", dataIndex: "reason", key: "h-reason", render: (v) => v || "-" },
  ];

  const hasWarning = availability?.hasAvailabilityWarning;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card title="Team Leave Approvals" className="card-professional" style={{ margin: 16 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "pending",
              label: "Pending Approvals",
              children: (
                <Table
                  className="custom-table"
                  rowKey="leaveRequestId"
                  loading={loading}
                  columns={columns}
                  dataSource={Array.isArray(pendingRequests) ? pendingRequests : []}
                  pagination={{ pageSize: 8 }}
                  locale={{ emptyText: "No pending leave requests from your team" }}
                />
              ),
            },
            {
              key: "history",
              label: "Team Leave History",
              children: (
                <>
                  <Space wrap style={{ marginBottom: 16 }}>
                    <Select
                      value={filterYear}
                      style={{ width: 120 }}
                      onChange={setFilterYear}
                      options={YEAR_OPTIONS}
                    />
                    <Select
                      value={filterStatus}
                      style={{ width: 160 }}
                      onChange={setFilterStatus}
                      options={STATUS_OPTIONS}
                    />
                    <span>
                      <Switch checked={filterUpcoming} onChange={setFilterUpcoming} /> Upcoming only
                    </span>
                  </Space>
                  <Table
                    className="custom-table"
                    rowKey="leaveRequestId"
                    loading={historyLoading}
                    columns={historyColumns}
                    dataSource={Array.isArray(teamRequests) ? teamRequests : []}
                    pagination={{ pageSize: 8 }}
                    locale={{ emptyText: "No leave requests found" }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        title="Leave Request"
        open={drawerOpen}
        onClose={closeDrawer}
        width={460}
        destroyOnClose
      >
        {selected && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Employee">{employeeName(selected)}</Descriptions.Item>
              <Descriptions.Item label="Leave Type">
                {selected.leaveTypeName ||
                  selected.policyName ||
                  LeaveTypeEnumLabel[selected.leaveType] ||
                  selected.leaveType ||
                  "-"}
              </Descriptions.Item>
              <Descriptions.Item label="From">{fmt(selected.fromDate)}</Descriptions.Item>
              <Descriptions.Item label="To">{fmt(selected.toDate)}</Descriptions.Item>
              <Descriptions.Item label="Days">{dayCount(selected)}</Descriptions.Item>
              {splitText(selected, "paid") && (
                <Descriptions.Item label="Paid">{splitText(selected, "paid")}</Descriptions.Item>
              )}
              {splitText(selected, "unpaid") && (
                <Descriptions.Item label="Unpaid">
                  {splitText(selected, "unpaid")}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Reason">{selected.reason || "-"}</Descriptions.Item>
            </Descriptions>

            {Number(selected.unpaidDays) > 0 && (
              <Alert
                type="info"
                showIcon
                message={`${selected.unpaidDays} day(s) of this request are unpaid. Only the paid portion is deducted from the leave balance; payroll calculates the final deduction.`}
              />
            )}

            {availabilityLoading ? (
              <Spin />
            ) : hasWarning ? (
              <Alert
                type="warning"
                showIcon
                message="Team availability overlap"
                description={
                  <div>
                    {(availability.overlaps || []).map((o, i) => (
                      <div key={i}>
                        {fmt(o.date)} -{" "}
                        {(o.employees || [])
                          .map((e) => `${e.employeeName} is already off`)
                          .join(", ")}
                      </div>
                    ))}
                  </div>
                }
              />
            ) : availability ? (
              <Alert
                type="success"
                showIcon
                message="No approved team leave overlaps with this request"
              />
            ) : null}

            {/* AI Coverage Risk (Jun-21). Advisory only - approval stays enabled. */}
            {availability?.coverageRiskInsight ? (
              <Alert
                type={riskAlertType(resolveCoverageRiskLevel(availability))}
                showIcon
                message={`Coverage Risk: ${titleCase(resolveCoverageRiskLevel(availability)) || "Not assessed"}`}
                description={
                  <div>
                    {availability.coverageRiskInsight.warning && (
                      <div>{availability.coverageRiskInsight.warning}</div>
                    )}
                    {availability.coverageRiskInsight.businessImpact && (
                      <div style={{ marginTop: 4 }}>
                        <b>Impact:</b> {availability.coverageRiskInsight.businessImpact}
                      </div>
                    )}
                    {Array.isArray(availability.coverageRiskInsight.overlapEmployees) &&
                      availability.coverageRiskInsight.overlapEmployees.length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          <b>Also off:</b>{" "}
                          {availability.coverageRiskInsight.overlapEmployees.join(", ")}
                        </div>
                      )}
                    {availability.coverageRiskInsight.recommendation && (
                      <div style={{ marginTop: 4 }}>
                        <b>Recommendation:</b> {availability.coverageRiskInsight.recommendation}
                      </div>
                    )}
                    {(availability.coverageRiskInsight.recommendedBackup ||
                      availability.coverageRiskInsight.recommended_backup) && (
                      <div style={{ marginTop: 4 }}>
                        <b>Suggested backup:</b>{" "}
                        {availability.coverageRiskInsight.recommendedBackup ||
                          availability.coverageRiskInsight.recommended_backup}
                      </div>
                    )}
                    {availability.coverageRiskInsight.decisionHint && (
                      <div style={{ marginTop: 4, fontStyle: "italic" }}>
                        {availability.coverageRiskInsight.decisionHint}
                      </div>
                    )}
                  </div>
                }
              />
            ) : availability && availability.isCoverageRiskAiAvailable === false ? (
              <Alert
                type="info"
                showIcon
                message={availability.coverageRiskMessage || "Coverage risk insight is unavailable."}
              />
            ) : null}

            <div>
              <Text strong>Remarks</Text>
              <TextArea
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional when approving, required when rejecting"
                maxLength={500}
                showCount
              />
            </div>

            <Space>
              <Button type="primary" loading={reviewing} onClick={() => handleReview(true)}>
                Approve
              </Button>
              <Button danger loading={reviewing} onClick={() => handleReview(false)}>
                Reject
              </Button>
            </Space>
          </Space>
        )}
      </Drawer>
    </DashboardLayout>
  );
};

export default TeamLeaveApprovals;
